import { useRef } from "react";
import { Modal, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import moment from "moment";
import logo from "assets/images/logo.png";

const formatAED = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `AED ${num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${num.toLocaleString("en-US")} KG`;
};

const InfoRow = ({ label, value }) => (
  <div className="merchant-invoice-modal__info-row">
    <span className="merchant-invoice-modal__info-label">{label}</span>
    <span className="merchant-invoice-modal__info-value">{value || "-"}</span>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="merchant-invoice-modal__detail-row">
    <span className="merchant-invoice-modal__detail-label">{label}</span>
    <span className="merchant-invoice-modal__detail-value">{value || "-"}</span>
  </div>
);

const InvoiceModal = ({ open, rowData, onClose, mode }) => {
  const invoiceRef = useRef(null);
  const handleDownloadPdf = async () => {
    const element = invoiceRef.current;
    if (!element || !rowData) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;

      const canvasRatio = canvas.width / canvas.height;
      const pageRatio = maxWidth / maxHeight;

      let renderWidth;
      let renderHeight;

      if (canvasRatio > pageRatio) {
        renderWidth = maxWidth;
        renderHeight = maxWidth / canvasRatio;
      } else {
        renderHeight = maxHeight;
        renderWidth = maxHeight * canvasRatio;
      }

      const x = margin + (maxWidth - renderWidth) / 2;
      const y = margin + (maxHeight - renderHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);
      pdf.save(`invoice-${rowData.orderIndex || rowData.id || "order"}.pdf`);
    } catch {
      window.print();
    }
  };

  if (!rowData) return null;

  return (
    <Modal
      open={open}
      footer={null}
      width={715}
      onCancel={onClose}
      className="merchant-invoice-modal"
      destroyOnClose
      closable={false}
      centered
    >
      <div className="merchant-invoice-modal__wrapper">
        <div ref={invoiceRef} className="merchant-invoice-modal__capture">
          <div className="merchant-invoice-modal__top">
            <img
              src={logo}
              alt="GreenMatter"
              className="merchant-invoice-modal__logo"
            />
            <h2 className="merchant-invoice-modal__title">
              {mode === "invoice" ? "Invoice" : "Withdrawal Invoice"}
            </h2>
          </div>

          <div className="merchant-invoice-modal__info-section">
            <InfoRow label="Client Name" value={rowData?.fullName} />
            {mode === "invoice" ? (
              <InfoRow
                label="Date"
                value={
                  rowData.creationDate
                    ? moment(rowData.creationDate).format("DD/MM/YYYY")
                    : "-"
                }
              />
            ) : (
              <InfoRow
                label="Date"
                value={
                  rowData.deliveryDate
                    ? moment(rowData.deliveryDate).format("DD/MM/YYYY")
                    : "-"
                }
              />
            )}

            {mode === "withdraw-invoice" ? (
              <InfoRow
                label="Withdrawal Amount in AED"
                value={formatAED(rowData?.supplyTotalPrice)}
              />
            ) : null}

            {mode === "invoice" ? (
              <>
                <InfoRow
                  label="Supplier Name"
                  value={rowData?.supplierName || "N/A"}
                />
                <InfoRow
                  label="Transaction Type"
                  value={rowData?.transactionType}
                />
                <InfoRow
                  label="transaction NO"
                  value={rowData?.transactionNO}
                />
              </>
            ) : null}
          </div>

          <div className="merchant-invoice-modal__divider" />

          {mode === "invoice" ? (
            <div className="merchant-invoice-modal__details-section">
              <DetailRow
                label="Quantity in KG"
                value={formatQuantity(rowData.quantity)}
              />
              <DetailRow
                label="Price per kg"
                value={formatAED(Number(rowData?.unitPrice || 0))}
              />
              <DetailRow
                label="wallet amount paid"
                value={formatAED(rowData?.walletDeduction)}
              />
              <DetailRow
                label="Bank Transfer amount paid"
                value={formatAED(rowData?.price - rowData?.walletDeduction)}
              />

              <div className="merchant-invoice-modal__total-row">
                <span className="merchant-invoice-modal__total-label">
                  Total Value in AED
                </span>
                <span className="merchant-invoice-modal__total-value">
                  {formatAED(rowData?.price)}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="merchant-invoice-modal__actions">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            className="merchant-invoice-modal__download-btn"
            onClick={handleDownloadPdf}
          >
            Download
          </Button>
          <button
            type="button"
            className="merchant-invoice-modal__close-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceModal;
